import React, { useMemo, useState } from "react";
import { findingsColumns } from "../../import/findings-display";
import { duplicateRowNumbers } from "../../import/extraction-to-preview";
import { INTENTS } from "../../import/intent";

const PAGE = 60;

const FILTERS = [
  { id: "all", label: "All" },
  { id: "gaps", label: "Needs attention" },
  { id: "registered", label: "Registered" },
  { id: "duplicates", label: "Duplicates" },
];

const cellValue = (passenger, key) => {
  if (key === "studentName") return passenger.name || "";
  if (key === "school") return passenger.schoolName || "";
  if (key === "address") return passenger.addressGetOn || "";
  if (key === "pickupRoute") return passenger.pickupRouteName || "";
  if (key === "dropoffRoute") return passenger.dropoffRouteName || "";
  if (key === "parentName") return passenger.parents?.[0]?.name || "";
  if (key === "parentPhone") return passenger.parents?.[0]?.phone || passenger.phone || "";
  const match = /^parent(\d+)(Name|Phone)$/.exec(key);
  if (match) {
    const parent = passenger.parents?.[Number(match[1]) - 1];
    if (!parent) return "";
    return match[2] === "Name" ? parent.name || "" : parent.phone || "";
  }
  return "";
};

const patchFor = (passenger, key, value) => {
  if (key === "studentName") return { name: value };
  if (key === "school") return { schoolName: value };
  if (key === "address") return { addressGetOn: value, addressGetOff: passenger.addressGetOff === passenger.addressGetOn ? value : passenger.addressGetOff };
  if (key === "pickupRoute") return { pickupRouteName: value };
  if (key === "dropoffRoute") return { dropoffRouteName: value };
  const slot = key === "parentName" || key === "parentPhone" ? 1 : Number((/^parent(\d+)/.exec(key) || [])[1]);
  const field = /Phone$/.test(key) ? "phone" : "name";
  const parents = [...(passenger.parents || [])];
  while (parents.length < slot) parents.push({ name: "", phone: "", email: "" });
  parents[slot - 1] = { ...parents[slot - 1], [field]: value };
  return { parents };
};

const statusFor = (passenger, matchEntry, duplicates) => {
  if (!passenger.name) return { id: "noname", label: "No name" };
  if (duplicates.has(passenger.rowNumber)) return { id: "duplicate", label: "Duplicate in file" };
  if (matchEntry?.confidence === "high") return { id: "registered", label: "Registered" };
  if (matchEntry) return { id: "possible", label: "Looks familiar" };
  return { id: "new", label: "New" };
};

const rowDecisionLabel = (intent) => (intent === INTENTS.ROUTES_ONLY ? "Place on service" : "Leave as is");

const PassengerTable = ({
  extraction,
  match,
  intent,
  existingMode,
  existingDecisions = {},
  onUpdate,
  onRemove,
  onRemoveMany,
  onDecision,
  undoStack = [],
  onUndo,
  isLoggedIn,
}) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(PAGE);

  const passengers = extraction?.passengers || [];
  const columns = useMemo(() => findingsColumns(extraction), [extraction]);
  const duplicates = useMemo(() => duplicateRowNumbers(passengers), [passengers]);
  const matches = match?.matches || {};
  const showDecisions = Boolean(isLoggedIn && (match?.summary?.existing || match?.summary?.possible));

  const rows = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("tr");
    return passengers
      .map((passenger) => ({ passenger, status: statusFor(passenger, matches[passenger.rowNumber], duplicates) }))
      .filter(({ passenger, status }) => {
        if (filter === "gaps" && !(passenger.missing || []).some((field) => field !== "school") && status.id !== "noname") return false;
        if (filter === "registered" && status.id !== "registered" && status.id !== "possible") return false;
        if (filter === "duplicates" && status.id !== "duplicate") return false;
        if (!needle) return true;
        const blob = [
          passenger.name,
          passenger.addressGetOn,
          passenger.schoolName,
          passenger.pickupRouteName,
          ...(passenger.parents || []).flatMap((parent) => [parent.name, parent.phone]),
        ]
          .join(" ")
          .toLocaleLowerCase("tr");
        return blob.includes(needle);
      });
  }, [passengers, filter, query, matches, duplicates]);

  const nameless = passengers.filter((passenger) => !passenger.name).map((passenger) => passenger.rowNumber);
  const duplicateList = [...duplicates];
  const visible = rows.slice(0, limit);
  const lastUndo = undoStack[undoStack.length - 1];
  // Without an account every row would just say "New" — only show status when it carries information.
  const showStatus = Boolean(isLoggedIn || nameless.length || duplicateList.length);

  return (
    <section className="start-list" aria-labelledby="start-list-title">
      <header className="start-list__head">
        <div>
          <h3 id="start-list-title">
            {passengers.filter((passenger) => passenger.name).length} passenger{passengers.length === 1 ? "" : "s"}
          </h3>
          <p className="start-muted">Fix anything inline or remove a row. Only what you see here is saved.</p>
        </div>
        <div className="start-list__tools">
          <input
            type="search"
            className="start-list__search"
            placeholder="Search names, phones, addresses"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search passengers"
          />
          <div className="start-list__filters" role="tablist" aria-label="Filter passengers">
            {FILTERS.filter((entry) => entry.id !== "registered" || showDecisions)
              .filter((entry) => entry.id !== "duplicates" || duplicateList.length > 0)
              .map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  role="tab"
                  aria-selected={filter === entry.id}
                  className={`start-chip${filter === entry.id ? " is-selected" : ""}`}
                  onClick={() => setFilter(entry.id)}
                >
                  {entry.label}
                </button>
              ))}
          </div>
        </div>
      </header>

      {(nameless.length > 0 || duplicateList.length > 0) && (
        <div className="start-list__bulk">
          {nameless.length > 0 && (
            <button type="button" className="start-text-link" onClick={() => onRemoveMany(nameless, "rows without a name")}>
              Remove {nameless.length} row{nameless.length === 1 ? "" : "s"} without a name
            </button>
          )}
          {duplicateList.length > 0 && (
            <button type="button" className="start-text-link" onClick={() => onRemoveMany(duplicateList, "duplicate rows")}>
              Remove {duplicateList.length} duplicate{duplicateList.length === 1 ? "" : "s"}
            </button>
          )}
        </div>
      )}

      {lastUndo && (
        <div className="start-undo" role="status">
          <span>
            Removed {lastUndo.removed.length} {lastUndo.label || (lastUndo.removed.length === 1 ? "row" : "rows")}
            {lastUndo.removed.length === 1 && lastUndo.removed[0].name ? ` · ${lastUndo.removed[0].name}` : ""}
          </span>
          <button type="button" className="start-text-link" onClick={onUndo}>
            Undo
          </button>
        </div>
      )}

      <div className="start-table-wrap start-table-wrap--edit">
        <table className="start-table start-table--edit">
          <thead>
            <tr>
              {showStatus && <th className="start-table__status">Status</th>}
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
              {showDecisions && <th>Registered student</th>}
              <th className="start-table__actions" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {visible.map(({ passenger, status }) => {
              const matchEntry = matches[passenger.rowNumber];
              const decision = existingDecisions[String(passenger.rowNumber)]?.action || "";
              const effective =
                decision || (matchEntry?.confidence === "high" ? existingMode : matchEntry ? "add" : "");
              return (
                <tr key={passenger.rowNumber} className={`is-${status.id}`}>
                  {showStatus && (
                    <td className="start-table__status">
                      <span className={`start-badge start-badge--${status.id}`}>{status.label}</span>
                    </td>
                  )}
                  {columns.map((column) => (
                    <td key={column.key}>
                      <input
                        type="text"
                        className="start-cell"
                        value={cellValue(passenger, column.key)}
                        aria-label={`${column.label}, row ${passenger.rowNumber}`}
                        onChange={(event) => onUpdate(passenger.rowNumber, patchFor(passenger, column.key, event.target.value))}
                      />
                    </td>
                  ))}
                  {showDecisions && (
                    <td>
                      {matchEntry ? (
                        <select
                          className="start-cell start-cell--select"
                          value={effective}
                          aria-label={`Registered student decision, row ${passenger.rowNumber}`}
                          title={`${matchEntry.studentName}${matchEntry.schoolName ? ` · ${matchEntry.schoolName}` : ""} · ${matchEntry.reasons.join(", ")}`}
                          onChange={(event) => onDecision(passenger.rowNumber, event.target.value)}
                        >
                          {intent === INTENTS.ROUTES_ONLY ? (
                            <>
                              <option value="link">{rowDecisionLabel(intent)}</option>
                              <option value="skip">Skip this row</option>
                            </>
                          ) : (
                            <>
                              <option value="skip">{rowDecisionLabel(intent)}</option>
                              <option value="update">Refresh details</option>
                              <option value="add">Add as new</option>
                            </>
                          )}
                        </select>
                      ) : (
                        <span className="start-muted">—</span>
                      )}
                    </td>
                  )}
                  <td className="start-table__actions">
                    <button
                      type="button"
                      className="start-row-remove"
                      aria-label={`Remove row ${passenger.rowNumber}${passenger.name ? ` (${passenger.name})` : ""}`}
                      title="Remove this row"
                      onClick={() => onRemove(passenger.rowNumber)}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
            {!visible.length && (
              <tr>
                <td colSpan={columns.length + 1 + (showStatus ? 1 : 0) + (showDecisions ? 1 : 0)} className="start-muted start-table__empty">
                  {passengers.length ? "Nothing matches this filter." : "No passengers in this file."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {rows.length > limit && (
        <button type="button" className="start-text-link start-list__more" onClick={() => setLimit(rows.length)}>
          Show all {rows.length} rows
        </button>
      )}
    </section>
  );
};

export default PassengerTable;
